// controllers
import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';

// grid objects
// import GraphMaterialsDot from './dot';
// import GraphMaterialsGroup from './group';

// static constants
import TimerEvent from '../../../bits/timer/timer-event';

// helpers
import { getElementIndex, isIE, mq } from '../../../assets/js/helpers';

export default class GraphMaterials {

	static selector = 'graph-materials';

	// class variables

	el = {};

	controllers = {
		animation: null,
		resize: null
	};

	colors = {
		green: '#5FB251', // 70%
		blackShark: '#2A2A2D',
		greyPaleSky: '#68717A',
		greyChateau: '#A9B0B8',
		white: '#FFFFFF'
	};

	coords = {
		hero: null,
		footer: null
	};

	data = {
		config: null
	};

	flags = {
		isMobile: false
	};

	graph = {
		canvas: null,
		ctx: null,

		config: {
			graph: null,
			steps: null
		},

		groups: [],
		fonts: {},
		state: {},
		measurements: {}
	};

	states = {
		activeItem: null,
		activeStep: null,
		activeStepIndex: 0,
		currentTime: 0,
		duration: 20000 // TODO: make dynamic
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			canvas: container.querySelector('#canvas'),
			controls: container.querySelector(`.${this.constructor.selector}__controls`),
			graph: container.querySelector(`.${this.constructor.selector}__graph`),
			graphCanvas: container.querySelector(`.${this.constructor.selector}__graph canvas`)
		};

		this.data.config = JSON.parse(decodeURIComponent(this.el.graph.getAttribute('data-config')));
		this.el.graph.removeAttribute('data-config');

		// set essential graph variables

		this.graph.config.graph = this.data.config.graph;
		this.graph.config.steps = this.data.config.steps;

		this.graph.canvas = this.el.graphCanvas;
		this.graph.ctx = this.graph.canvas.getContext('2d');

		//

		this.initialize();
	}

	// init

	initialize() {
		this.#addEventListeners();
		this.changeStepIndex(0);
	}

	// event listeners

	#addEventListeners = () => {
		this.el.controls.addEventListener(TimerEvent.CHANGING, this.#onTimerItemChanging);
		this.el.controls.addEventListener(TimerEvent.POLLING, this.#onTimerItemPolling);
		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.POLL_STATUS));

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});
	}

	#removeEventListeners = () => {
		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onTimerItemChanging = (e) => {
		const $target = e.detail.target;
		this.checkCurrentItem($target);

	}

	#onTimerItemPolling = (e) => {
		const $target = e.detail.target;
		this.checkCurrentItem($target);
	}

	checkCurrentItem($target) {
		if (this.states.activeItem !== $target) {
			const index = getElementIndex($target.parentElement.children, $target);
			this.changeStepIndex(index);
			this.states.activeItem = $target;
		}
	}

	// controller methods

	#onAnimationUpdate = () => true;

	#onAnimationRender = () => {
		// this.renderGraph();
	}

	#onResizeUpdate = () => {
		if (ResizeController.coords.window.width !== parseInt(this.el.graphCanvas.getAttribute('width'))
			|| ResizeController.coords.window.height !== parseInt(this.el.graphCanvas.getAttribute('height'))) {
			return true;
		}
	}

	#onResizeRender = () => {

		if (mq('medium')) {
			this.el.graphCanvas.style.width = `${this.el.graph.offsetWidth}px`;
			this.el.graphCanvas.style.height = `${this.el.graph.offsetHeight}px`;
			this.el.graphCanvas.style.transform = '';
		} else {
			this.el.graphCanvas.style.width = `${this.el.graph.offsetWidth * 1.5}px`;
			this.el.graphCanvas.style.height = `${this.el.graph.offsetHeight * 1.5}px`;
			this.el.graphCanvas.style.transformOrigin = '0% 0%';
			this.el.graphCanvas.style.transform = 'scale(0.66667)';
		}

		this.adjustCanvas();
		this.measureGraph();
		this.renderGraph();
	}

	// render canvas methods

	measureGraph() {
		const { canvas, ctx } = this.graph;

		// font labels
		this.graph.fonts.mono = {};
		this.graph.fonts.mono.value = 'normal normal 12px MaisonNeueMono';
		this.graph.fonts.mono.measurement = ctx.measureText(this.graph.fonts.mono.value);
		this.graph.fonts.mono.height = isIE() ? 12 : this.graph.fonts.mono.measurement.actualBoundingBoxAscent + this.graph.fonts.mono.measurement.actualBoundingBoxDescent;

		this.graph.fonts.sans = {};
		this.graph.fonts.sans.value = 'normal 500 12px MaisonNeue';
		this.graph.fonts.sans.measurement = ctx.measureText(this.graph.fonts.sans.value);
		this.graph.fonts.sans.height = isIE() ? 12 : this.graph.fonts.sans.measurement.actualBoundingBoxAscent + this.graph.fonts.sans.measurement.actualBoundingBoxDescent;

		// graph measurements
		this.graph.measurements.axisOffsetLeftX = 70;
		this.graph.measurements.axisOffsetRightX = 20;
		this.graph.measurements.axisOffsetBottomY = 60;
		this.graph.measurements.axisOffsetTopY = 10;
		this.graph.measurements.axisWidthX = canvas.width - this.graph.measurements.axisOffsetLeftX - this.graph.measurements.axisOffsetRightX;
		this.graph.measurements.axisHeightY = canvas.height - this.graph.measurements.axisOffsetBottomY - this.graph.measurements.axisOffsetTopY;

		// step
		this.states.activeStep = this.graph.config.steps[this.states.activeStepIndex];
		this.graph.measurements.stepXMax = parseFloat(this.states.activeStep.config.xMax);
		this.graph.measurements.stepYMax = parseFloat(this.states.activeStep.config.yMax);
		this.graph.measurements.stepIncrementX = parseFloat(this.states.activeStep.config.incrementX);
		this.graph.measurements.stepIncrementY = parseFloat(this.states.activeStep.config.incrementY);
		this.graph.measurements.stepIncrementWidth = this.graph.measurements.axisWidthX / Math.floor(this.graph.measurements.stepXMax / this.graph.measurements.stepIncrementX);
		this.graph.measurements.stepIncrementHeight = (this.graph.measurements.axisHeightY - this.graph.fonts.mono.height) / Math.floor(this.graph.measurements.stepYMax / this.graph.measurements.stepIncrementY);
	}

	measureGroups() {
		// shortcuts
		const { ctx } = this.graph;
		const configSteps = this.graph.config.steps;

		// dots
		ctx.fillStyle = this.colors.green;
		ctx.globalAlpha = 0.7;

		// remove old groups

		for (let i = 0; i < this.graph.groups.length; i++) {
			const group = this.graph.groups[i];
			group.destroy();
		}

		// create new groups

		for (let k = 0; k < configSteps.length; k++) {

			const stepValues = configSteps[k];
			// const dots = [];

			const groupConfig = {};
			groupConfig.headingContainmentPadding = parseFloat(stepValues.config.dotGroupHeadingContainmentPadding);
			groupConfig.headingMinimumIncrementX = parseFloat(stepValues.config.dotGroupHeadingMinimumIncrementX);
			groupConfig.headingMaximumIncrementX = parseFloat(stepValues.config.dotGroupHeadingMaximumIncrementX);
			groupConfig.showDotHeadings = groupConfig.headingMinimumIncrementX <= this.graph.measurements.stepIncrementX && groupConfig.headingMaximumIncrementX >= this.graph.measurements.stepIncrementX;

			// only render group if visible in current step

			let labelGroupInThisStep = false;
			for (const labelGroupConfig in stepValues.config.showGroupHeadingInSteps) {
				if (parseFloat(stepValues.config.showGroupHeadingInSteps[labelGroupConfig].value) === this.states.activeStepIndex) {
					labelGroupInThisStep = true;
					break;
				}
			}

			groupConfig.labelGroupInThisStep = labelGroupInThisStep;

			let showDotsInThisStep = false;
			for (const showStepConfig in stepValues.config.showPointsInSteps) {
				if (parseFloat(stepValues.config.showPointsInSteps[showStepConfig].value) === this.states.activeStepIndex) {
					showDotsInThisStep = true;
					break;
				}
			}

			// TODO: render dots separately from measuring
			// ie configure all dots above this point from all steps
			// move dot calculations to up here
			// reference dots based on show / hide logic above

			groupConfig.showDotsInThisStep = showDotsInThisStep;

			if (groupConfig.showDotsInThisStep) {
				groupConfig.pointBounds = {
					point1: {
						x: null,
						y: null
					},
					point2: {
						x: null,
						y: null
					},
					distance: null
				};

				groupConfig.rectBounds = {
					top: null,
					right: null,
					bottom: null,
					left: null
				};

				for (let i = 0; i < stepValues.points.length; i++) {
					const dot = {};
					dot.values = stepValues.points[i];
					dot.valueX = parseFloat(dot.values.xAxisValue); // scientific measurement, not pixel coordinate
					dot.valueY = parseFloat(dot.values.yAxisValue); // scientific measurement, not pixel coordinate
					dot.x = this.graph.measurements.axisOffsetLeftX + dot.valueX / this.graph.measurements.stepXMax * this.graph.measurements.axisWidthX;
					dot.y = this.graph.measurements.axisOffsetTopY + this.graph.measurements.axisHeightY - dot.valueY / this.graph.measurements.stepYMax * this.graph.measurements.axisHeightY;

					// find point bounds

					const getPointDistance = (point1, point2) => Math.sqrt(((point2.x - point1.x) ** 2) + ((point2.y - point1.y) ** 2));

					if (groupConfig.pointBounds.point1.x === null || groupConfig.pointBounds.point1.y === null) {
						groupConfig.pointBounds.point1.x = dot.x;
						groupConfig.pointBounds.point1.y = dot.y;
					} else if (groupConfig.pointBounds.point2.x === null || groupConfig.pointBounds.point2.y === null) {
						groupConfig.pointBounds.point2.x = dot.x;
						groupConfig.pointBounds.point2.y = dot.y;
						groupConfig.pointBounds.distance = getPointDistance(groupConfig.pointBounds.point1, groupConfig.pointBounds.point2);
					} else {
						const point1Distance = getPointDistance(groupConfig.pointBounds.point1, { x: dot.x, y: dot.y });
						const point2Distance = getPointDistance(groupConfig.pointBounds.point2, { x: dot.x, y: dot.y });

						if (point1Distance > groupConfig.pointBounds.distance) {
							groupConfig.pointBounds.point2 = {
								x: dot.x,
								y: dot.y
							};
							groupConfig.pointBounds.distance = point1Distance;
						} else if (point2Distance > groupConfig.pointBounds.distance) {
							groupConfig.pointBounds.point1 = {
								x: dot.x,
								y: dot.y
							};
							groupConfig.pointBounds.distance = point2Distance;
						}
					}

					// find rectangular bounds

					if (groupConfig.rectBounds.left === null || dot.x < groupConfig.rectBounds.left) {
						groupConfig.rectBounds.left = dot.x;
					}

					if (groupConfig.rectBounds.top === null || dot.y < groupConfig.rectBounds.top) {
						groupConfig.rectBounds.top = dot.y;
					}

					if (groupConfig.rectBounds.bottom === null || dot.y > groupConfig.rectBounds.bottom) {
						groupConfig.rectBounds.bottom = dot.y;
					}

					if (groupConfig.rectBounds.right === null || dot.x > groupConfig.rectBounds.right) {
						groupConfig.rectBounds.right = dot.x;
					}

					// draw dots

					ctx.save();
					ctx.beginPath();
					ctx.arc(dot.x, dot.y, 5, 0, 2 * Math.PI);

					if (stepValues.config.dotStyle.value === 'stroke') {
						if (dot.values.isFeatured === true) {
							ctx.strokeStyle = this.colors.green;
							ctx.globalAlpha = 0.7;
						} else {
							ctx.strokeStyle = this.colors.greyChateau;
						}
						ctx.lineWidth = 2;
						ctx.stroke();
					} else {
						ctx.globalAlpha = 0.7;
						ctx.fill();
					}

					if (groupConfig.showDotHeadings && dot.values.headingPlacement) {
						let textX = 0;
						let textY = 0;

						ctx.font = this.graph.fonts.sans.value;
						ctx.strokeStyle = this.colors.greyChateau;
						ctx.lineWidth = 1;
						ctx.beginPath();

						switch (dot.values.headingPlacement.value) {
							case 'topLeft':
							ctx.moveTo(dot.x - 7, dot.y - 7);
							ctx.lineTo(dot.x - 22, dot.y - 22);
							ctx.lineTo(dot.x - 32, dot.y - 22);
							ctx.textAlign = 'right';
							textX = dot.x - 37;
							textY = dot.y - 22 + this.graph.fonts.sans.height / 2 - 1;
							break;

							case 'bottomRight':
							ctx.moveTo(dot.x + 7, dot.y + 7);
							ctx.lineTo(dot.x + 22, dot.y + 22);
							ctx.lineTo(dot.x + 32, dot.y + 22);
							ctx.textAlign = 'left';
							textX = dot.x + 37;
							textY = dot.y + 22 + this.graph.fonts.sans.height / 2 - 1;
							break;

							case 'right':
							default:
							ctx.moveTo(dot.x + 10, dot.y);
							ctx.lineTo(dot.x + 30, dot.y);
							ctx.textAlign = 'left';
							textX = dot.x + 35;
							textY = dot.y + this.graph.fonts.sans.height / 2 - 1;
							break;
						}

						ctx.stroke();

						ctx.fillStyle = this.colors.greyChateau;

						const headingNewlines = dot.values.heading.replace(/\+ /g, '+\\n');
						const headingSplit = headingNewlines.split('\\n');

						for (let p = 0; p < headingSplit.length; p++) {
							const splitValue = headingSplit[p];
							ctx.fillText(splitValue, textX, textY + this.graph.fonts.sans.height * 1.5 * p);
						}
					}

					ctx.restore();
				}

				// calculate rectangular bounding area of points

				ctx.save();

				if (groupConfig.labelGroupInThisStep) {

					ctx.beginPath();
					ctx.setLineDash([3, 3]);
					ctx.strokeStyle = this.colors.greyPaleSky;
					ctx.lineWidth = 1;

					if (stepValues.config.dotGroupHeadingContainmentType.value === 'rectangle') {
						// draw rectangle around dots
						const shapeBounds = {
							top: Math.round(Math.max(groupConfig.rectBounds.top - groupConfig.headingContainmentPadding, this.graph.measurements.axisOffsetTopY)),
							right: Math.round(Math.min(groupConfig.rectBounds.right + groupConfig.headingContainmentPadding, this.graph.measurements.axisOffsetLeftX + this.graph.measurements.axisWidthX)),
							bottom: Math.round(Math.min(groupConfig.rectBounds.bottom + groupConfig.headingContainmentPadding, this.graph.measurements.axisOffsetTopY + this.graph.measurements.axisHeightY)),
							left: Math.round(Math.max(groupConfig.rectBounds.left - groupConfig.headingContainmentPadding, this.graph.measurements.axisOffsetLeftX))
						};

						ctx.strokeRect(shapeBounds.left, shapeBounds.top, shapeBounds.right - shapeBounds.left, shapeBounds.bottom - shapeBounds.top);

					}
				}

				ctx.restore();
			}
		}

		ctx.globalAlpha = 1;
	}

	renderAxes() {
		const { ctx } = this.graph;

		// axis lines
		ctx.fillStyle = this.colors.greyChateau;
		ctx.fillRect(this.graph.measurements.axisOffsetLeftX, this.graph.measurements.axisHeightY + this.graph.measurements.axisOffsetTopY, this.graph.measurements.axisWidthX, 1);
		ctx.fillRect(this.graph.measurements.axisOffsetLeftX, this.graph.measurements.axisHeightY + this.graph.measurements.axisOffsetTopY, 1, `-${this.graph.measurements.axisHeightY}`);
	}

	adjustCanvas() {
		const { canvas } = this.graph;

		// set canvas dimensions
		canvas.setAttribute('width', `${canvas.offsetWidth}px`);
		canvas.setAttribute('height', `${canvas.offsetHeight}px`);

		this.clearCanvas();
	}

	clearCanvas() {
		const { canvas, ctx } = this.graph;

		// fill safe area
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.fillStyle = this.colors.blackShark;
		ctx.fillRect(0, 0, canvas.width, canvas.height);
	}

	renderLabels() {
		const { canvas, ctx } = this.graph;
		const configGraph = this.graph.config.graph;

		// y axis (rotated)
		ctx.globalAlpha = 1;
		ctx.fillStyle = this.colors.greyChateau;
		ctx.font = this.graph.fonts.sans.value;
		ctx.textAlign = 'left';

		ctx.save();
		ctx.rotate(Math.PI / 2 * 3);
		ctx.fillText(`${configGraph.yAxisLabel}`, `-${this.graph.measurements.axisHeightY}`, this.graph.fonts.sans.height);
		ctx.restore();

		// x axis
		ctx.fillText(`${configGraph.xAxisLabel}`, 70, `${canvas.height - 5}`);

		// axis number font
		ctx.font = this.graph.fonts.mono.value;

		// x axis numbers
		ctx.fillStyle = this.colors.greyPaleSky;
		ctx.textAlign = 'center';

		const incrementsX = parseInt(this.graph.measurements.stepXMax / this.graph.measurements.stepIncrementX) + 1;
		for (let i = 0; i <= incrementsX; i++) {
			ctx.fillText(`${this.graph.measurements.stepIncrementX * i}`, `${this.graph.measurements.axisOffsetLeftX + this.graph.measurements.stepIncrementWidth * i}`, `${canvas.height - 40}`);
		}

		// y axis numbers
		ctx.fillStyle = this.colors.greyPaleSky;
		ctx.textAlign = 'right';

		const incrementsY = parseInt(this.graph.measurements.stepYMax / this.graph.measurements.stepIncrementY) + 1;
		for (let i = 0; i <= incrementsY; i++) {
			ctx.fillText(`${this.graph.measurements.stepIncrementY * i}`, 55, `${this.graph.measurements.axisHeightY + this.graph.measurements.axisOffsetTopY - this.graph.measurements.stepIncrementHeight * i}`);
		}
	}

	renderDots() {
		// shortcuts
		const { ctx } = this.graph;
		const configSteps = this.graph.config.steps;

		// dots
		ctx.fillStyle = this.colors.green;
		ctx.globalAlpha = 0.7;

		for (let k = 0; k < configSteps.length; k++) {
			const valueGroup = configSteps[k];
			const dotGroupHeadingContainmentPadding = parseFloat(valueGroup.config.dotGroupHeadingContainmentPadding);
			// const dotGroupHeadingFontSize = `${parseInt(valueGroup.config.dotGroupHeadingFontSize)}px`;
			const dotGroupHeadingMinimumIncrementX = parseFloat(valueGroup.config.dotGroupHeadingMinimumIncrementX);
			const dotGroupHeadingMaximumIncrementX = parseFloat(valueGroup.config.dotGroupHeadingMaximumIncrementX);
			const showDotHeadings = dotGroupHeadingMinimumIncrementX <= this.graph.measurements.stepIncrementX && dotGroupHeadingMaximumIncrementX >= this.graph.measurements.stepIncrementX;

			// only render group if visible in current step

			let labelGroupInThisStep = false;
			for (const labelGroupConfig in valueGroup.config.showGroupHeadingInSteps) {
				if (parseFloat(valueGroup.config.showGroupHeadingInSteps[labelGroupConfig].value) === this.states.activeStepIndex) {
					labelGroupInThisStep = true;
					break;
				}
			}

			let showDotsInThisStep = false;
			for (const showStepConfig in valueGroup.config.showPointsInSteps) {
				if (parseFloat(valueGroup.config.showPointsInSteps[showStepConfig].value) === this.states.activeStepIndex) {
					showDotsInThisStep = true;
					break;
				}
			}

			if (showDotsInThisStep) {
				const pointBounds = {
					point1: {
						x: null,
						y: null
					},
					point2: {
						x: null,
						y: null
					},
					distance: null
				};

				const rectBounds = {
					top: null,
					right: null,
					bottom: null,
					left: null
				};

				for (let i = 0; i < valueGroup.points.length; i++) {
					const dot = valueGroup.points[i];
					const dotXAxisValue = parseFloat(dot.xAxisValue);
					const dotYAxisValue = parseFloat(dot.yAxisValue);
					const dotX = this.graph.measurements.axisOffsetLeftX + dotXAxisValue / this.graph.measurements.stepXMax * this.graph.measurements.axisWidthX;
					const dotY = this.graph.measurements.axisOffsetTopY + this.graph.measurements.axisHeightY - dotYAxisValue / this.graph.measurements.stepYMax * this.graph.measurements.axisHeightY;

					// find point bounds

					const getPointDistance = (point1, point2) => Math.sqrt(((point2.x - point1.x) ** 2) + ((point2.y - point1.y) ** 2));

					if (pointBounds.point1.x === null || pointBounds.point1.y === null) {
						pointBounds.point1.x = dotX;
						pointBounds.point1.y = dotY;
					} else if (pointBounds.point2.x === null || pointBounds.point2.y === null) {
						pointBounds.point2.x = dotX;
						pointBounds.point2.y = dotY;
						pointBounds.distance = getPointDistance(pointBounds.point1, pointBounds.point2);
					} else {
						const point1Distance = getPointDistance(pointBounds.point1, { x: dotX, y: dotY });
						const point2Distance = getPointDistance(pointBounds.point2, { x: dotX, y: dotY });

						if (point1Distance > pointBounds.distance) {
							pointBounds.point2 = {
								x: dotX,
								y: dotY
							};
							pointBounds.distance = point1Distance;
						} else if (point2Distance > pointBounds.distance) {
							pointBounds.point1 = {
								x: dotX,
								y: dotY
							};
							pointBounds.distance = point2Distance;
						}
					}

					// find rectangular bounds

					if (rectBounds.left === null || dotX < rectBounds.left) {
						rectBounds.left = dotX;
					}

					if (rectBounds.top === null || dotY < rectBounds.top) {
						rectBounds.top = dotY;
					}

					if (rectBounds.bottom === null || dotY > rectBounds.bottom) {
						rectBounds.bottom = dotY;
					}

					if (rectBounds.right === null || dotX > rectBounds.right) {
						rectBounds.right = dotX;
					}

					// draw dots

					ctx.save();
					ctx.beginPath();
					ctx.arc(dotX, dotY, 5, 0, 2 * Math.PI);

					if (valueGroup.config.dotStyle.value === 'stroke') {
						if (dot.isFeatured === true) {
							ctx.strokeStyle = this.colors.green;
							ctx.globalAlpha = 0.7;
						} else {
							ctx.strokeStyle = this.colors.greyChateau;
						}
						ctx.lineWidth = 2;
						ctx.stroke();
					} else {
						ctx.globalAlpha = 0.7;
						ctx.fill();
					}

					if (showDotHeadings && dot.headingPlacement) {
						let textX = 0;
						let textY = 0;

						ctx.font = this.graph.fonts.sans.value;
						ctx.strokeStyle = this.colors.greyChateau;
						ctx.lineWidth = 1;
						ctx.beginPath();

						switch (dot.headingPlacement.value) {
							case 'topLeft':
							ctx.moveTo(dotX - 7, dotY - 7);
							ctx.lineTo(dotX - 22, dotY - 22);
							ctx.lineTo(dotX - 32, dotY - 22);
							ctx.textAlign = 'right';
							textX = dotX - 37;
							textY = dotY - 22 + this.graph.fonts.sans.height / 2 - 1;
							break;

							case 'bottomRight':
							ctx.moveTo(dotX + 7, dotY + 7);
							ctx.lineTo(dotX + 22, dotY + 22);
							ctx.lineTo(dotX + 32, dotY + 22);
							ctx.textAlign = 'left';
							textX = dotX + 37;
							textY = dotY + 22 + this.graph.fonts.sans.height / 2 - 1;
							break;

							case 'right':
							default:
							ctx.moveTo(dotX + 10, dotY);
							ctx.lineTo(dotX + 30, dotY);
							ctx.textAlign = 'left';
							textX = dotX + 35;
							textY = dotY + this.graph.fonts.sans.height / 2 - 1;
							break;
						}

						ctx.stroke();

						ctx.fillStyle = this.colors.greyChateau;

						const headingNewlines = dot.heading.replace(/\+ /g, '+\\n');
						const headingSplit = headingNewlines.split('\\n');

						for (let p = 0; p < headingSplit.length; p++) {
							const splitValue = headingSplit[p];
							ctx.fillText(splitValue, textX, textY + this.graph.fonts.sans.height * 1.5 * p);
						}
					}

					ctx.restore();
				}

				// calculate rectangular bounding area of points

				ctx.save();

				if (labelGroupInThisStep) {

					ctx.beginPath();
					ctx.setLineDash([3, 3]);
					ctx.strokeStyle = this.colors.greyPaleSky;
					ctx.lineWidth = 1;

					if (valueGroup.config.dotGroupHeadingContainmentType.value === 'rectangle') {
						// draw rectangle around dots
						const shapeBounds = {
							top: Math.round(Math.max(rectBounds.top - dotGroupHeadingContainmentPadding, this.graph.measurements.axisOffsetTopY)),
							right: Math.round(Math.min(rectBounds.right + dotGroupHeadingContainmentPadding, this.graph.measurements.axisOffsetLeftX + this.graph.measurements.axisWidthX)),
							bottom: Math.round(Math.min(rectBounds.bottom + dotGroupHeadingContainmentPadding, this.graph.measurements.axisOffsetTopY + this.graph.measurements.axisHeightY)),
							left: Math.round(Math.max(rectBounds.left - dotGroupHeadingContainmentPadding, this.graph.measurements.axisOffsetLeftX))
						};

						ctx.strokeRect(shapeBounds.left, shapeBounds.top, shapeBounds.right - shapeBounds.left, shapeBounds.bottom - shapeBounds.top);

					}
				}

				ctx.restore();
			}
		}

		ctx.globalAlpha = 1;
	}

	renderGraph() {
		this.clearCanvas();
		this.renderLabels();
		this.measureGroups(); // this.renderDots();
		this.renderAxes();
	}

	changeStepIndex(stepIndex = 0) {
		this.states.activeStepIndex = stepIndex;
		this.clearCanvas();
		this.measureGraph();
		// this.measureGroups();
		this.renderGraph();
	}

	// destroy

	destroy() {
		this.#removeEventListeners();
	}

}
